import { z } from "zod/v4";
import { localeSchema, profileSchema, themePreferenceSchema } from "../schemas/profile";
import {
	banReasonLength,
	MAX_BAN_REASON_LEN,
	MAX_GENDER_CHANGES,
	MAX_SKILL_CHANGES,
	skillLevelSchema,
	userGenderSchema,
} from "../schemas/shared";
import type { ListParams } from "./games";
import { fetchApi, fetchVoid, paginatedSchema, toSearchParams } from "./internal";

export const MAX_SKILL_REASON_LENGTH = 1000;
// Refined rather than .max(): zod counts UTF-16 units and the server counts
// Unicode scalars, so an emoji-bearing note would be refused here and accepted there.
export const skillChangeReasonSchema = z
	.string()
	.trim()
	.min(1)
	.refine((reason) => banReasonLength(reason) <= MAX_SKILL_REASON_LENGTH, {
		message: `reason must be at most ${MAX_SKILL_REASON_LENGTH} characters`,
	});

const adminSkillRequestSchema = z.object({
	reason: skillChangeReasonSchema,
	skill_changes_remaining: z.number().int().min(0).max(MAX_SKILL_CHANGES).optional(),
	skill_level: skillLevelSchema.optional(),
});

export type AdminSkillRequest = z.infer<typeof adminSkillRequestSchema>;

const adminGenderRequestSchema = z.object({
	gender: userGenderSchema.optional(),
	gender_changes_remaining: z.number().int().min(0).max(MAX_GENDER_CHANGES).optional(),
});

export type AdminGenderRequest = z.infer<typeof adminGenderRequestSchema>;

/**
 * Partial identity edit. Every field is optional and an omitted one is left
 * alone, so the caller sends only what it changed.
 *
 * `bio`, `city` and `instagram_handle` accept the empty string on purpose -
 * that is how the server is told to clear the column. `display_name` and
 * `username` cannot: the server refuses an empty value, and trimming before
 * the length check stops whitespace buying the way past it.
 */
const adminProfileRequestSchema = z
	.object({
		bio: z.string().optional(),
		city: z.string().optional(),
		display_name: z.string().trim().min(1).optional(),
		instagram_handle: z.string().optional(),
		is_looking_for_game: z.boolean().optional(),
		locale: localeSchema.optional(),
		onboarded: z.boolean().optional(),
		theme_preference: themePreferenceSchema.optional(),
		username: z.string().trim().min(1).optional(),
	})
	.refine((body) => Object.keys(body).length > 0, {
		message: "at least one field must be given",
	});

export type AdminProfileRequest = z.infer<typeof adminProfileRequestSchema>;

// The reason is required, not defaulted: it is the only record of why an
// account lost access, and an empty one reads identically to a bug that dropped
// it. Trimmed before the length check so whitespace cannot buy room.
const banProfileRequestSchema = z.object({
	// Refined rather than .max(): zod counts UTF-16 units and the server counts
	// runes, so an emoji-bearing note would be refused here and accepted there.
	reason: z
		.string()
		.trim()
		.min(1)
		.refine((reason) => banReasonLength(reason) <= MAX_BAN_REASON_LEN, {
			message: `reason must be at most ${MAX_BAN_REASON_LEN} characters`,
		}),
});

export type BanProfileRequest = z.infer<typeof banProfileRequestSchema>;

export const profiles = {
	ban: (id: string, reason: string) =>
		fetchApi(`/admin/profiles/${id}/ban`, profileSchema, {
			body: JSON.stringify(banProfileRequestSchema.parse({ reason })),
			method: "POST",
		}),
	// 204 with no body: the account is anonymised into a tombstone server-side,
	// so there is no profile left to hand back.
	delete: (id: string) => fetchVoid(`/admin/profiles/${id}`, { method: "DELETE" }),
	deleteAvatar: (id: string) =>
		fetchApi(`/admin/profiles/${id}/avatar`, profileSchema, { method: "DELETE" }),
	deleteBanner: (id: string) =>
		fetchApi(`/admin/profiles/${id}/banner`, profileSchema, { method: "DELETE" }),
	get: (id: string) => fetchApi(`/admin/profiles/${id}`, profileSchema),
	list: (params: ListParams = {}) => {
		const qs = toSearchParams(params);
		return fetchApi(`/admin/profiles${qs ? `?${qs}` : ""}`, paginatedSchema(profileSchema));
	},
	// No body: the server owns which columns a reset clears. Answers with the
	// profile rather than 204, so the detail page re-renders from the response
	// instead of guessing what the reset touched.
	resetOnboarding: (id: string) =>
		fetchApi(`/admin/profiles/${id}/onboarding`, profileSchema, { method: "DELETE" }),
	// No body: the ban is lifted wholesale or not at all.
	unban: (id: string) => fetchApi(`/admin/profiles/${id}/ban`, profileSchema, { method: "DELETE" }),
	update: (id: string, body: AdminProfileRequest) =>
		fetchApi(`/admin/profiles/${id}`, profileSchema, {
			body: JSON.stringify(adminProfileRequestSchema.parse(body)),
			method: "PATCH",
		}),
	updateGender: (id: string, body: AdminGenderRequest) =>
		fetchApi(`/admin/profiles/${id}/gender`, profileSchema, {
			body: JSON.stringify(adminGenderRequestSchema.parse(body)),
			method: "PATCH",
		}),
	updateSkill: (id: string, body: AdminSkillRequest) =>
		fetchApi(`/admin/profiles/${id}/skill`, profileSchema, {
			body: JSON.stringify(adminSkillRequestSchema.parse(body)),
			method: "PUT",
		}),
};
